import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 2rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 100vh; /* 100vh pour occuper exactement toute la hauteur du viewport */
    min-height: unset; /* Suppression de la hauteur minimale */
  }
`;

const GradientBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 50%, 
    rgba(0, 0, 0, 0), 
    ${({ theme }) => `${theme.colors.background}30`});
  z-index: -1;
`;

const HeroContent = styled(motion.div)`
  max-width: 800px;
  z-index: 1;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  
  .desktop-only {
    display: block;
  }
  
  .mobile-only {
    display: none;
  }
  
  @media (max-width: 768px) {
    .desktop-only {
      display: none;
    }
    
    .mobile-only {
      display: block;
    }
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto 2.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ScrollDownIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  /* Largeur à 100% pour garantir que l'élément occupe toute la largeur disponible */
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
    margin: 0 auto; /* Centre l'svg horizontalement */
    fill: ${({ theme }) => theme.colors.textSecondary};
  }
  
  span {
    font-size: 0.9rem;
    margin-top: 0.5rem;
    color: ${({ theme }) => theme.colors.textSecondary};
    width: 100%; /* Assure que le texte occupe toute la largeur du parent */
    text-align: center; /* Centre le texte */
  }
`;

const ScrollDownContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  
  @media (max-width: 768px) {
    bottom: 5vh; /* Plus éloigné du contenu sur mobile */
  }
  
  @media (max-height: 700px) {
    bottom: 2vh; /* Encore plus bas pour les écrans moins hauts */
  }
`;

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
    margin-top: 0; /* Suppression de la marge supérieure */
  }
`;

const AboutContainer = styled.div<{ hasProfileImage: boolean }>`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${props => props.hasProfileImage ? '1fr 1fr' : '1fr'};
  gap: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled(motion.div)<{ hasProfileImage: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    order: 2; /* Makes content appear after the image on mobile */
  }
`;

const AboutTitle = styled(motion.h2)<{ hasProfileImage: boolean }>`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    text-align: center;
  }
`;

const AboutText = styled(motion.p)`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: center;
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  
  @media (max-width: 768px) {
    order: 1; /* Makes image appear before the content on mobile */
    max-width: 70%;
    margin: 0 auto;
    aspect-ratio: 1;
  }
  
  @media (max-width: 480px) {
    max-width: 85%;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right bottom, ${({ theme }) => theme.colors.primary}30, ${({ theme }) => theme.colors.accent}30);
    z-index: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99]
    }
  }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const HomePage: React.FC = () => {
  // Utilisation des namespaces "home" et "common" pour les traductions de cette page
  const { t, i18n } = useTranslation();
  const [profileImageExists, setProfileImageExists] = React.useState(false);
  
  React.useEffect(() => {
    // Check if profile image exists
    const img = new Image();
    img.src = '/images/profile-photo.jpg';
    img.onload = () => setProfileImageExists(true);
    img.onerror = () => setProfileImageExists(false);
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    const navbar = document.querySelector('header') || document.querySelector('nav'); // Sélectionne la navbar
    
    if (aboutSection) {
      const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
      const aboutRect = aboutSection.getBoundingClientRect();
      const offsetPosition = aboutRect.top + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <HeroSection>
        <GradientBackground />
        <HeroContent
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
            <Subtitle variants={fadeInUp}>
              <span className="desktop-only">{t('home:hero.subtitle')}</span>
              <span className="mobile-only" dangerouslySetInnerHTML={{ __html: t('home:hero.subtitleMobile') }} />
            </Subtitle>
          <HeroTitle variants={fadeInUp}>{t('home:hero.title')}</HeroTitle>
          <Description variants={fadeInUp}>
            {t('home:hero.description')}
          </Description>
        </HeroContent>
        
        <ScrollDownContainer>
          <ScrollDownIndicator
            onClick={scrollToAbout}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.5 }}
            whileHover={{ y: 5 }}
          >
            <motion.svg 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24"
              animate={{ y: [0, 5, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </motion.svg>
            <span>{t('home:hero.scrollDown')}</span>
          </ScrollDownIndicator>
        </ScrollDownContainer>
      </HeroSection>
      
      <AboutSection id="about">
        <AboutContainer hasProfileImage={profileImageExists}>
            <AboutContent
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
              hasProfileImage={profileImageExists}
            >
            <AboutTitle variants={fadeInUp} hasProfileImage={profileImageExists}>{t('home:about.title')}</AboutTitle>
            <AboutText variants={fadeInUp}>
              {t('home:about.paragraph1')}
            </AboutText>
            <AboutText variants={fadeInUp}>
              {t('home:about.paragraph2')}
            </AboutText>
            <AboutText variants={fadeInUp}>
              {t('home:about.paragraph3')}
            </AboutText>
            </AboutContent>
          
          {profileImageExists && (
            <ImageContainer
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <img src="/images/profile-photo.jpg" alt={t('home:about.imageAlt')} />
            </ImageContainer>
          )}
        </AboutContainer>
      </AboutSection>
    </>
  );
};

export default HomePage;